import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  X, User, Mail, Calendar, DollarSign, Users, TrendingUp, 
  Award, CreditCard, Clock, CheckCircle, XCircle, Eye,
  Copy, ExternalLink, Building2, Shield
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const ViewAsUserModal = ({ token, userId, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    fetchUserDashboard();
  }, [userId]);

  const fetchUserDashboard = async () => {
    try {
      const response = await axios.get(
        `${API}/api/admin/view-as-user/${userId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setData(response.data);
    } catch (error) {
      toast.error('Error al cargar datos del usuario');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('es-MX', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copiado al portapapeles');
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
        <div className="bg-gray-900 rounded-2xl p-8">
          <div className="animate-spin w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full mx-auto"></div>
          <p className="text-white mt-4">Cargando dashboard del usuario...</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  const { user, stats, pool_share, referrals, commissions, badges, payout_requests, author_verification, works } = data;

  return (
    <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-gray-900 border border-purple-500/30 rounded-2xl max-w-6xl w-full max-h-[95vh] overflow-y-auto my-4">
        {/* Header */}
        <div className="sticky top-0 bg-gray-900 border-b border-purple-500/30 p-6 z-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-indigo-500 flex items-center justify-center text-white text-2xl gotham-bold">
                {user.full_name?.charAt(0) || 'U'}
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="gotham-bold text-2xl text-white">{user.full_name}</h2>
                  <span className={`px-2 py-0.5 rounded text-xs ${
                    user.user_type === 'author' 
                      ? 'bg-purple-500/20 text-purple-400' 
                      : 'bg-blue-500/20 text-blue-400'
                  }`}>
                    {user.user_type === 'author' ? 'Autor' : 'Audio-Lector'}
                  </span>
                </div>
                <p className="text-white/60 flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  {user.email}
                </p>
                <p className="text-white/40 text-sm">@{user.username || 'sin-username'}</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <span className={`px-3 py-1 rounded-full text-sm ${
                  user.membership_status === 'active' 
                    ? 'bg-green-500/20 text-green-400' 
                    : 'bg-red-500/20 text-red-400'
                }`}>
                  {user.membership_status === 'active' ? '✓ Activo' : '✗ Inactivo'}
                </span>
              </div>
              <button 
                onClick={onClose}
                className="text-white/60 hover:text-white p-2 hover:bg-white/10 rounded-lg"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>
          
          {/* Admin Notice */}
          <div className="mt-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 flex items-center gap-2">
            <Eye className="w-5 h-5 text-yellow-400" />
            <p className="text-yellow-400 text-sm">
              <strong>Vista de Admin:</strong> Estás viendo el dashboard de este usuario. Esta vista es solo de lectura.
            </p>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* User Details */}
          <div className="grid md:grid-cols-4 gap-4">
            <Card className="card-viubux-dark border-purple-500/30">
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white/60 text-xs">Código de Referido</p>
                    <p className="text-purple-400 font-mono text-lg flex items-center gap-2">
                      {user.referral_code || 'N/A'}
                      {user.referral_code && (
                        <button onClick={() => copyToClipboard(user.referral_code)} className="hover:text-purple-300">
                          <Copy className="w-4 h-4" />
                        </button>
                      )}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="card-viubux-dark border-green-500/30">
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white/60 text-xs">Código de Acceso</p>
                    <p className="text-green-400 font-mono text-lg flex items-center gap-2">
                      {user.access_code || 'Sin código'}
                      {user.access_code && (
                        <button onClick={() => copyToClipboard(user.access_code)} className="hover:text-green-300">
                          <Copy className="w-4 h-4" />
                        </button>
                      )}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="card-viubux-dark border-blue-500/30">
              <CardContent className="pt-4">
                <div>
                  <p className="text-white/60 text-xs">Suscripción Expira</p>
                  <p className="text-blue-400 text-sm">
                    {user.subscription_expires_at ? formatDate(user.subscription_expires_at) : 'N/A'}
                  </p>
                </div>
              </CardContent>
            </Card>
            
            <Card className="card-viubux-dark border-white/20">
              <CardContent className="pt-4">
                <div>
                  <p className="text-white/60 text-xs">Registro</p>
                  <p className="text-white/80 text-sm">{formatDate(user.created_at)}</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Stats Cards */}
          <div className="grid md:grid-cols-4 gap-4">
            <Card className="card-viubux-dark border-green-500/30">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white/60 text-sm">Ganancias Totales</p>
                    <p className="text-green-400 gotham-bold text-2xl">{formatCurrency(stats.total_earnings)}</p>
                  </div>
                  <DollarSign className="w-10 h-10 text-green-400/30" />
                </div>
              </CardContent>
            </Card>
            
            <Card className="card-viubux-dark border-yellow-500/30">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white/60 text-sm">Bono Rápido</p>
                    <p className="text-yellow-400 gotham-bold text-2xl">{formatCurrency(stats.rapid_bonus_earnings)}</p>
                  </div>
                  <TrendingUp className="w-10 h-10 text-yellow-400/30" />
                </div>
              </CardContent>
            </Card>
            
            <Card className="card-viubux-dark border-purple-500/30">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white/60 text-sm">Pool de Autores</p>
                    <p className="text-purple-400 gotham-bold text-2xl">{formatCurrency(stats.pool_earnings)}</p>
                  </div>
                  <Building2 className="w-10 h-10 text-purple-400/30" />
                </div>
              </CardContent>
            </Card>
            
            <Card className="card-viubux-dark border-blue-500/30">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white/60 text-sm">Referidos</p>
                    <p className="text-blue-400 gotham-bold text-2xl">
                      {stats.active_referrals} <span className="text-white/40 text-sm">/ {stats.total_referrals}</span>
                    </p>
                  </div>
                  <Users className="w-10 h-10 text-blue-400/30" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Pool Info for Authors */}
          {pool_share && (
            <Card className="card-viubux-dark border-purple-500/30">
              <CardHeader className="pb-2">
                <CardTitle className="text-white text-lg flex items-center gap-2">
                  <Award className="w-5 h-5 text-purple-400" />
                  Participación en Pool de Autores
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-6">
                  <div className="bg-purple-500/20 px-4 py-2 rounded-lg">
                    <p className="text-white/60 text-xs">Plan Actual</p>
                    <p className="text-purple-400 gotham-bold">{pool_share.plan} ({pool_share.percentage}%)</p>
                  </div>
                  <div>
                    <p className="text-white/60 text-xs">Referidos Activos</p>
                    <p className="text-white gotham-bold">{pool_share.active_referrals}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Author Verification */}
          {user.user_type === 'author' && (
            <Card className="card-viubux-dark border-yellow-500/30">
              <CardHeader className="pb-2">
                <CardTitle className="text-white text-lg flex items-center gap-2">
                  <Shield className="w-5 h-5 text-yellow-400" />
                  Verificación de Autor
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-4">
                  <span className={`px-3 py-1 rounded-full text-sm ${
                    user.author_verification_status === 'approved' ? 'bg-green-500/20 text-green-400' :
                    user.author_verification_status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                    user.author_verification_status === 'rejected' ? 'bg-red-500/20 text-red-400' :
                    'bg-white/10 text-white/60'
                  }`}>
                    {user.author_verification_status === 'approved' ? '✓ Verificado' :
                     user.author_verification_status === 'pending' ? '⏳ Pendiente' :
                     user.author_verification_status === 'rejected' ? '✗ Rechazado' :
                     'Sin solicitud'}
                  </span>
                  {user.subsidized && (
                    <span className="px-3 py-1 rounded-full text-sm bg-blue-500/20 text-blue-400">
                      💰 Inscripción Subsidiada
                    </span>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Badges */}
          {badges && badges.length > 0 && (
            <Card className="card-viubux-dark border-purple-500/30">
              <CardHeader className="pb-2">
                <CardTitle className="text-white text-lg flex items-center gap-2">
                  <Award className="w-5 h-5 text-purple-400" />
                  Insignias
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-3">
                  {badges.map((badge, idx) => (
                    <div
                      key={idx}
                      className="flex items-center gap-2 px-3 py-1.5 rounded-full"
                      style={{ backgroundColor: `${badge.color}20`, border: `1px solid ${badge.color}50` }}
                    >
                      <span className="text-lg">{badge.icon}</span>
                      <span className="text-white text-sm">{badge.name}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Referrals Table */}
          <Card className="card-viubux-dark border-purple-500/30">
            <CardHeader className="pb-2">
              <CardTitle className="text-white text-lg flex items-center gap-2">
                <Users className="w-5 h-5 text-blue-400" />
                Referidos ({referrals?.length || 0})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {referrals && referrals.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-white/10">
                        <th className="text-left text-white/60 py-2 px-2">Nombre</th>
                        <th className="text-left text-white/60 py-2 px-2">Tipo</th>
                        <th className="text-left text-white/60 py-2 px-2">Estado</th>
                        <th className="text-right text-white/60 py-2 px-2">Sub-referidos</th>
                        <th className="text-right text-white/60 py-2 px-2">Ganancias</th>
                      </tr>
                    </thead>
                    <tbody>
                      {referrals.slice(0, 10).map(ref => (
                        <tr key={ref.id} className="border-b border-white/5">
                          <td className="py-2 px-2">
                            <p className="text-white">{ref.full_name}</p>
                            <p className="text-white/40 text-xs">{ref.email}</p>
                          </td>
                          <td className="py-2 px-2">
                            <span className={`px-2 py-0.5 rounded text-xs ${
                              ref.user_type === 'author' ? 'bg-purple-500/20 text-purple-400' : 'bg-blue-500/20 text-blue-400'
                            }`}>
                              {ref.user_type === 'author' ? 'Autor' : 'Audio-Lector'}
                            </span>
                          </td>
                          <td className="py-2 px-2">
                            <span className={`px-2 py-0.5 rounded text-xs ${
                              ref.membership_status === 'active' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                            }`}>
                              {ref.membership_status === 'active' ? 'Activo' : 'Inactivo'}
                            </span>
                          </td>
                          <td className="py-2 px-2 text-right text-white/60">{ref.direct_referrals}</td>
                          <td className="py-2 px-2 text-right text-green-400">{formatCurrency(ref.total_earnings)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {referrals.length > 10 && (
                    <p className="text-white/40 text-center text-sm mt-2">
                      Mostrando 10 de {referrals.length} referidos
                    </p>
                  )}
                </div>
              ) : (
                <p className="text-white/40 text-center py-4">Sin referidos</p>
              )}
            </CardContent>
          </Card>

          {/* Commissions Table */}
          <Card className="card-viubux-dark border-green-500/30">
            <CardHeader className="pb-2">
              <CardTitle className="text-white text-lg flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-green-400" />
                Comisiones ({commissions?.length || 0})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {commissions && commissions.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-white/10">
                        <th className="text-left text-white/60 py-2 px-2">Tipo</th>
                        <th className="text-left text-white/60 py-2 px-2">Descripción</th>
                        <th className="text-left text-white/60 py-2 px-2">Estado</th>
                        <th className="text-right text-white/60 py-2 px-2">Monto</th>
                      </tr>
                    </thead>
                    <tbody>
                      {commissions.slice(0, 10).map((com, idx) => (
                        <tr key={idx} className="border-b border-white/5">
                          <td className="py-2 px-2 text-yellow-400">
                            {com.commission_type === 'rapid_bonus' ? 'Bono Rápido' : com.commission_type}
                          </td>
                          <td className="py-2 px-2 text-white/60">{com.description || 'N/A'}</td>
                          <td className="py-2 px-2">
                            <span className={`px-2 py-0.5 rounded text-xs ${
                              com.status === 'paid' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'
                            }`}>
                              {com.status === 'paid' ? 'Pagado' : 'Pendiente'}
                            </span>
                          </td>
                          <td className="py-2 px-2 text-right text-green-400">{formatCurrency(com.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {commissions.length > 10 && (
                    <p className="text-white/40 text-center text-sm mt-2">
                      Mostrando 10 de {commissions.length} comisiones
                    </p>
                  )}
                </div>
              ) : (
                <p className="text-white/40 text-center py-4">Sin comisiones</p>
              )}
            </CardContent>
          </Card>

          {/* Payout Requests */}
          {payout_requests && payout_requests.length > 0 && (
            <Card className="card-viubux-dark border-yellow-500/30">
              <CardHeader className="pb-2">
                <CardTitle className="text-white text-lg flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-yellow-400" />
                  Solicitudes de Pago ({payout_requests.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {payout_requests.slice(0, 5).map((req, idx) => (
                    <div key={idx} className="bg-black/30 rounded-lg p-3 flex items-center justify-between">
                      <div>
                        <p className="text-white">{formatCurrency(req.amount)}</p>
                        <p className="text-white/40 text-xs">{formatDate(req.created_at)}</p>
                      </div>
                      <span className={`px-2 py-1 rounded text-xs ${
                        req.status === 'approved' ? 'bg-green-500/20 text-green-400' :
                        req.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                        'bg-red-500/20 text-red-400'
                      }`}>
                        {req.status === 'approved' ? 'Aprobado' :
                         req.status === 'pending' ? 'Pendiente' : 'Rechazado'}
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Obras Registradas (solo para autores) */}
          {user.user_type === 'author' && (
            <Card className="card-viubux-dark border-blue-500/30">
              <CardHeader className="pb-2">
                <CardTitle className="text-white text-lg flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-blue-400" />
                  Obras Registradas ({works?.length || 0})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {works && works.length > 0 ? (
                  <div className="space-y-3">
                    {works.map((work, idx) => (
                      <div key={idx} className="bg-black/30 rounded-lg p-4 border border-white/10">
                        <div className="flex items-start gap-4">
                          {work.cover_image && (
                            <img 
                              src={work.cover_image} 
                              alt={work.title}
                              className="w-16 h-20 object-cover rounded"
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          )}
                          <div className="flex-1">
                            <h4 className="text-white font-bold">{work.title}</h4>
                            <p className="text-white/50 text-xs font-mono mt-1">ISBN: {work.isbn}</p>
                            
                            {/* Tipo de contenido */}
                            {work.content_type && (
                              <span className="inline-block mt-1 px-2 py-0.5 rounded text-xs bg-indigo-500/20 text-indigo-400">
                                {work.content_type === 'audiobook' && '🎧 Audiolibro'}
                                {work.content_type === 'videobook' && '📹 Videolibro'}
                                {work.content_type === 'ebook' && '📖 eBook'}
                                {work.content_type === 'movie' && '🎬 Película'}
                                {work.content_type === 'music_video' && '🎵 Videoclip'}
                                {work.content_type === 'conference' && '🎤 Conferencia'}
                                {work.content_type === 'book_presentation' && '📚 Presentación'}
                                {work.content_type === 'booktrailer' && '🎬 Booktrailer'}
                                {work.content_type === 'podcast' && '🎙️ Podcast'}
                                {work.content_type === 'song' && '🎵 Canción'}
                                {work.content_type === 'meditation' && '🧘 Meditación'}
                                {!['audiobook', 'videobook', 'ebook', 'movie', 'music_video', 'conference', 'book_presentation', 'booktrailer', 'podcast', 'song', 'meditation'].includes(work.content_type) && work.content_type}
                              </span>
                            )}
                            
                            {/* Formatos */}
                            {work.formats && work.formats.length > 0 && (
                              <div className="flex gap-1 mt-2">
                                {work.formats.map((format, i) => (
                                  <span key={i} className="px-2 py-0.5 rounded text-xs bg-white/10 text-white/60">
                                    {format}
                                  </span>
                                ))}
                              </div>
                            )}
                            
                            {/* Co-productores */}
                            {work.has_coproduction && work.coproducers_detail && work.coproducers_detail.length > 0 && (
                              <div className="mt-3 p-2 bg-rose-500/10 rounded border border-rose-500/20">
                                <p className="text-rose-400 text-xs font-bold mb-1">🤝 Co-Productores:</p>
                                <div className="space-y-1">
                                  <div className="flex items-center justify-between text-xs">
                                    <span className="text-white/70">Autor Principal ({user.full_name})</span>
                                    <span className="text-green-400 font-bold">{work.author_percentage?.toFixed(1) || (100 - work.coproducers_detail.reduce((sum, c) => sum + (c.percentage || 0), 0)).toFixed(1)}%</span>
                                  </div>
                                  {work.coproducers_detail.map((cp, i) => (
                                    <div key={i} className="flex items-center justify-between text-xs">
                                      <span className="text-white/70">{cp.full_name} <span className="text-white/40">({cp.role})</span></span>
                                      <span className="text-rose-400 font-bold">{cp.percentage}%</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            
                            {/* Link a Viubux */}
                            {work.public_url && (
                              <a 
                                href={work.public_url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 mt-2 text-xs text-blue-400 hover:text-blue-300"
                              >
                                <ExternalLink className="w-3 h-3" />
                                Ver en Viubux
                              </a>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-white/40 text-center py-4">Sin obras registradas</p>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-900 border-t border-purple-500/30 p-4">
          <Button onClick={onClose} className="w-full btn-viubux">
            Cerrar Vista
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ViewAsUserModal;
